// Hand-built SVG/HTML charts following the dataviz rules:
//   thin marks (2px lines, <=24px bars, 4px rounded data-end), hairline solid grid, one y-axis,
//   legend for >=2 series (+ sparing direct labels), crosshair tooltip on lines, per-mark tooltip on bars/cells,
//   text always in ink tokens (never the series color), table view provided by ChartCard.

import { type KeyboardEvent, type PointerEvent, type ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { niceTicks } from '../format.ts';

/** Categorical slot color (1-based, fixed order). Callers fold series past 8 into "Otros"; never cycle. */
export const slot = (n: number) => `var(--cat-${Math.min(Math.max(n, 1), 8)})`;
export const ordinal = (n: number) => `var(--ord-${Math.min(Math.max(n, 1), 5)})`;

const FALLBACK_WIDTH = 640;

export function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setWidth(Math.round(w));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width || FALLBACK_WIDTH] as const;
}

// ------------------------------------------------------------------ legend & tooltip

export interface LegendItem {
  label: string;
  color: string;
  kind?: 'line' | 'rect';
}

export function Legend({ items }: { items: LegendItem[] }) {
  return (
    <div className="legend">
      {items.map((it) => (
        <span key={it.label}>
          <i className={it.kind === 'rect' ? 'key-rect' : 'key-line'} style={{ background: it.color }} aria-hidden />
          {it.label}
        </span>
      ))}
    </div>
  );
}

export interface TooltipRow {
  label: string;
  value: string;
  color?: string;
}

export interface TooltipContent {
  title: string;
  rows: TooltipRow[];
  note?: string;
}

/** Positioned inside a relative container; flips to the left of the anchor near the right edge. */
export function Tooltip({ x, y, containerWidth, content }: { x: number; y: number; containerWidth: number; content: TooltipContent }) {
  const flip = x > containerWidth - 220;
  return (
    <div className="tooltip" role="status" style={{ left: flip ? x - 12 : x + 12, top: Math.max(0, y), transform: flip ? 'translateX(-100%)' : undefined }}>
      <div className="tt-title">{content.title}</div>
      {content.rows.map((r) => (
        <div className="tt-row" key={r.label}>
          {r.color && <i className="key-line" style={{ background: r.color }} aria-hidden />}
          <strong>{r.value}</strong>
          <span>{r.label}</span>
        </div>
      ))}
      {content.note && <div className="tt-note">{content.note}</div>}
    </div>
  );
}

function textWidth(s: string): number {
  return s.length * 6.4 + 4;
}

/** Indices of x labels that fit without overlapping (always first and last). */
function labelIndices(n: number, plotWidth: number, sample: string): number[] {
  if (n <= 0) return [];
  const per = Math.max(1, Math.ceil(n / Math.max(1, Math.floor(plotWidth / (textWidth(sample) + 18)))));
  const out: number[] = [];
  for (let i = 0; i < n; i += per) out.push(i);
  const last = n - 1;
  if (out[out.length - 1] !== last) {
    if (out.length > 1 && last - out[out.length - 1]! < per * 0.6) out.pop();
    out.push(last);
  }
  return out;
}

/** For value-positioned x (uneven gaps): keep a label only if it clears the previous one; the last always wins. */
function spacedLabels(n: number, pos: (i: number) => number, width: (i: number) => number): number[] {
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    const prev = out[out.length - 1];
    if (prev === undefined || pos(i) - pos(prev) >= (width(i) + width(prev)) / 2 + 6) out.push(i);
    else if (i === n - 1) out[out.length - 1] = i;
  }
  return out;
}

// ------------------------------------------------------------------ line chart

export interface LineSeries {
  key: string;
  label: string;
  values: (number | null)[];
  /** Categorical slot (1..8), fixed per entity. */
  slot: number;
  /** Confidence band drawn as a 10% wash. */
  band?: { low: (number | null)[]; high: (number | null)[] };
}

export interface LineChartProps {
  x: (string | number)[];
  series: LineSeries[];
  formatY: (v: number) => string;
  formatValue?: (v: number) => string;
  formatX?: (x: string | number) => string;
  /** Position x by value (e.g. retention day offsets) instead of evenly. */
  linearX?: boolean;
  height?: number;
  /** Upper bound for the axis (e.g. 1 for rates). */
  yCap?: number;
  ariaLabel: string;
  tooltipNote?: (index: number) => string | undefined;
}

export function LineChart(props: LineChartProps) {
  const { x, series, formatY, formatX = String, linearX, height = 220, yCap, ariaLabel } = props;
  const formatValue = props.formatValue ?? formatY;
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);

  const all = series.flatMap((s) => [...s.values, ...(s.band?.high ?? [])]).filter((v): v is number => v !== null && Number.isFinite(v));
  const dataMax = all.length ? Math.max(...all) : 0;
  const ticks = niceTicks(yCap !== undefined ? Math.min(dataMax || yCap, yCap) : dataMax || 1);
  const yMax = ticks[ticks.length - 1] ?? 1;

  const left = Math.max(...ticks.map((t) => textWidth(formatY(t)))) + 6;
  const top = 8;
  const bottom = 22;
  const plotH = height - top - bottom;
  const y = (v: number) => top + plotH - (v / yMax) * plotH;

  // Direct end labels for 2-4 series when they don't collide (the legend stays the dependable channel).
  const ends = series
    .map((s) => {
      let i = s.values.length - 1;
      while (i >= 0 && (s.values[i] === null || s.values[i] === undefined)) i--;
      return i >= 0 ? { s, i, yy: y(s.values[i] as number) } : null;
    })
    .filter((e): e is { s: LineSeries; i: number; yy: number } => e !== null);
  const sortedEnds = [...ends].sort((a, b) => a.yy - b.yy);
  const endLabels = series.length >= 2 && series.length <= 4 && ends.length === series.length && sortedEnds.every((e, k) => k === 0 || e.yy - sortedEnds[k - 1]!.yy >= 13);
  const right = endLabels ? Math.max(...series.map((s) => textWidth(s.label))) + 10 : 12;
  const plotW = Math.max(40, width - left - right);

  const xs = x.map((v) => (typeof v === 'number' ? v : 0));
  const minX = linearX ? Math.min(...xs) : 0;
  const maxX = linearX ? Math.max(...xs) : x.length - 1;
  const px = (i: number) => {
    if (x.length <= 1) return left + plotW / 2;
    const v = linearX ? xs[i]! : i;
    return left + ((v - minX) / (maxX - minX || 1)) * plotW;
  };

  const pathOf = (values: (number | null)[]) => {
    let d = '';
    let pen = false;
    values.forEach((v, i) => {
      if (v === null || v === undefined || !Number.isFinite(v)) {
        pen = false;
        return;
      }
      d += `${pen ? 'L' : 'M'}${px(i).toFixed(1)},${y(v).toFixed(1)}`;
      pen = true;
    });
    return d;
  };

  const bandOf = (band: NonNullable<LineSeries['band']>) => {
    const segs: string[] = [];
    let cur: number[] = [];
    const flush = () => {
      if (cur.length > 1) {
        const upper = cur.map((i) => `${px(i).toFixed(1)},${y(band.high[i]!).toFixed(1)}`);
        const lower = [...cur].reverse().map((i) => `${px(i).toFixed(1)},${y(band.low[i]!).toFixed(1)}`);
        segs.push(`M${upper.join('L')}L${lower.join('L')}Z`);
      }
      cur = [];
    };
    band.high.forEach((h, i) => {
      const l = band.low[i];
      if (h === null || h === undefined || l === null || l === undefined) flush();
      else cur.push(i);
    });
    flush();
    return segs.join('');
  };

  const nearest = (clientX: number, rect: DOMRect) => {
    const mx = clientX - rect.left;
    let best = 0;
    let dist = Infinity;
    for (let i = 0; i < x.length; i++) {
      const d = Math.abs(px(i) - mx);
      if (d < dist) {
        dist = d;
        best = i;
      }
    }
    return best;
  };

  const onMove = (e: PointerEvent<SVGRectElement>) => {
    const svg = e.currentTarget.ownerSVGElement;
    if (svg) setHover(nearest(e.clientX, svg.getBoundingClientRect()));
  };
  const onKey = (e: KeyboardEvent<SVGRectElement>) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      setHover((h) => Math.min(x.length - 1, Math.max(0, (h ?? x.length - 1) + (e.key === 'ArrowRight' ? 1 : -1))));
    } else if (e.key === 'Escape') setHover(null);
  };

  const xLabels = linearX ? spacedLabels(x.length, px, (i) => textWidth(formatX(x[i]!))) : labelIndices(x.length, plotW, formatX(x[0] ?? ''));
  const hx = hover !== null ? px(hover) : 0;

  return (
    <div className="chart" ref={ref}>
      {series.length >= 2 && <Legend items={series.map((s) => ({ label: s.label, color: slot(s.slot) }))} />}
      <svg width={width} height={height} role="img" aria-label={ariaLabel}>
        {ticks.map((t) => (
          <g key={t}>
            <line className={t === 0 ? 'baseline' : 'grid-line'} x1={left} x2={left + plotW} y1={y(t)} y2={y(t)} />
            <text className="tick" x={left - 6} y={y(t)} dy="0.32em" textAnchor="end">
              {formatY(t)}
            </text>
          </g>
        ))}
        {xLabels.map((i) => (
          <text key={i} className="tick" x={px(i)} y={height - 6} textAnchor={x.length > 1 && i === x.length - 1 ? 'end' : i === 0 && x.length > 1 ? 'start' : 'middle'}>
            {formatX(x[i]!)}
          </text>
        ))}
        {series.map((s) =>
          s.band ? <path key={`${s.key}-band`} d={bandOf(s.band)} style={{ fill: slot(s.slot), fillOpacity: 0.1 }} /> : null,
        )}
        {series.map((s) => (
          <path key={s.key} d={pathOf(s.values)} fill="none" style={{ stroke: slot(s.slot) }} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        ))}
        {series.map((s) => {
          // isolated points (no neighbours) would be invisible as a path: draw them as dots
          return s.values.map((v, i) =>
            v !== null && v !== undefined && (s.values[i - 1] ?? null) === null && (s.values[i + 1] ?? null) === null ? (
              <circle key={`${s.key}-${i}`} cx={px(i)} cy={y(v)} r={4} style={{ fill: slot(s.slot), stroke: 'var(--surface)' }} strokeWidth={2} />
            ) : null,
          );
        })}
        {endLabels &&
          ends.map((e) => (
            <text key={e.s.key} className="end-label" x={px(e.i) + 8} y={e.yy} dy="0.32em">
              {e.s.label}
            </text>
          ))}
        {hover !== null && (
          <g pointerEvents="none">
            <line className="crosshair" x1={hx} x2={hx} y1={top} y2={top + plotH} />
            {series.map((s) => {
              const v = s.values[hover];
              return v === null || v === undefined ? null : (
                <circle key={s.key} cx={hx} cy={y(v)} r={4} style={{ fill: slot(s.slot), stroke: 'var(--surface)' }} strokeWidth={2} />
              );
            })}
          </g>
        )}
        <rect
          className="plot-hit"
          x={left - 8}
          y={top}
          width={plotW + 16}
          height={plotH}
          tabIndex={0}
          aria-label={`${ariaLabel}: usa las flechas para recorrer los valores`}
          onPointerMove={onMove}
          onPointerLeave={() => setHover(null)}
          onFocus={() => setHover((h) => h ?? x.length - 1)}
          onBlur={() => setHover(null)}
          onKeyDown={onKey}
        />
      </svg>
      {hover !== null && (
        <Tooltip
          x={hx}
          y={top}
          containerWidth={width}
          content={{
            title: formatX(x[hover]!),
            rows: series.map((s) => {
              const v = s.values[hover];
              const b = s.band;
              const lo = b?.low[hover];
              const hi = b?.high[hover];
              const ci = lo !== null && lo !== undefined && hi !== null && hi !== undefined ? ` (IC ${formatValue(lo)}–${formatValue(hi)})` : '';
              return { label: s.label, value: v === null || v === undefined ? '—' : `${formatValue(v)}${ci}`, color: slot(s.slot) };
            }),
            note: props.tooltipNote?.(hover),
          }}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------------ column chart (single or stacked)

export interface ColumnSeries {
  key: string;
  label: string;
  values: number[];
  slot: number;
}

export interface ColumnChartProps {
  x: (string | number)[];
  series: ColumnSeries[];
  formatY: (v: number) => string;
  formatValue?: (v: number) => string;
  formatX?: (x: string | number) => string;
  height?: number;
  ariaLabel: string;
  /** Show the total in the tooltip of stacked columns. */
  totalLabel?: string;
  /** Label only the tallest column (selective labelling). */
  labelMax?: boolean;
}

const GAP = 2;
const RADIUS = 4;

/** Column with a 4px rounded data-end (top) and a square baseline. */
function columnPath(x: number, yTop: number, w: number, h: number, rounded: boolean): string {
  if (h <= 0) return '';
  const r = rounded ? Math.min(RADIUS, h, w / 2) : 0;
  const yb = yTop + h;
  if (!r) return `M${x},${yb}V${yTop}H${x + w}V${yb}Z`;
  return `M${x},${yb}V${yTop + r}Q${x},${yTop} ${x + r},${yTop}H${x + w - r}Q${x + w},${yTop} ${x + w},${yTop + r}V${yb}Z`;
}

export function ColumnChart(props: ColumnChartProps) {
  const { x, series, formatY, formatX = String, height = 200, ariaLabel } = props;
  const formatValue = props.formatValue ?? formatY;
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);

  const totals = x.map((_, i) => series.reduce((s, se) => s + (se.values[i] ?? 0), 0));
  const ticks = niceTicks(Math.max(0, ...totals) || 1);
  const yMax = ticks[ticks.length - 1] ?? 1;
  const left = Math.max(...ticks.map((t) => textWidth(formatY(t)))) + 6;
  const top = props.labelMax ? 18 : 8;
  const bottom = 22;
  const right = 8;
  const plotW = Math.max(40, width - left - right);
  const plotH = height - top - bottom;
  const band = plotW / Math.max(1, x.length);
  const barW = Math.max(2, Math.min(24, band - GAP));
  const y = (v: number) => top + plotH - (v / yMax) * plotH;
  const cx = (i: number) => left + band * i + band / 2;
  const maxIdx = totals.indexOf(Math.max(...totals));
  const xLabels = labelIndices(x.length, plotW, formatX(x[0] ?? ''));

  const onKey = (e: KeyboardEvent<SVGRectElement>, i: number) => {
    if (e.key === 'Escape') setHover(null);
    else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const next = Math.min(x.length - 1, Math.max(0, i + (e.key === 'ArrowRight' ? 1 : -1)));
      const el = (e.currentTarget.parentNode as SVGGElement | null)?.parentNode?.querySelectorAll<SVGRectElement>('rect.bar-hit')[next];
      el?.focus();
    }
  };

  return (
    <div className="chart" ref={ref}>
      {series.length >= 2 && <Legend items={series.map((s) => ({ label: s.label, color: slot(s.slot), kind: 'rect' }))} />}
      <svg width={width} height={height} role="img" aria-label={ariaLabel}>
        {ticks.map((t) => (
          <g key={t}>
            <line className={t === 0 ? 'baseline' : 'grid-line'} x1={left} x2={left + plotW} y1={y(t)} y2={y(t)} />
            <text className="tick" x={left - 6} y={y(t)} dy="0.32em" textAnchor="end">
              {formatY(t)}
            </text>
          </g>
        ))}
        {xLabels.map((i) => (
          <text key={i} className="tick" x={cx(i)} y={height - 6} textAnchor="middle">
            {formatX(x[i]!)}
          </text>
        ))}
        {x.map((_, i) => {
          let base = 0;
          const nonZero = series.map((s) => (s.values[i] ?? 0) > 0);
          const topIdx = nonZero.lastIndexOf(true);
          return (
            <g key={i}>
              <rect
                className="bar-hit"
                x={left + band * i}
                y={top}
                width={band}
                height={plotH}
                tabIndex={0}
                aria-label={`${formatX(x[i]!)}: ${series.map((s) => `${s.label} ${formatValue(s.values[i] ?? 0)}`).join(', ')}`}
                onPointerEnter={() => setHover(i)}
                onPointerLeave={() => setHover(null)}
                onFocus={() => setHover(i)}
                onBlur={() => setHover(null)}
                onKeyDown={(e) => onKey(e, i)}
              />
              <g className={`bar-group${hover === i ? ' hover' : ''}`} pointerEvents="none">
                {series.map((s, k) => {
                  const v = s.values[i] ?? 0;
                  if (v <= 0) return null;
                  const yTop = y(base + v);
                  const yBottom = y(base) - (base > 0 ? GAP : 0);
                  base += v;
                  return <path key={s.key} d={columnPath(cx(i) - barW / 2, yTop, barW, yBottom - yTop, k === topIdx)} style={{ fill: slot(s.slot) }} />;
                })}
              </g>
            </g>
          );
        })}
        {props.labelMax && totals[maxIdx]! > 0 && (
          <text className="end-label" x={cx(maxIdx)} y={y(totals[maxIdx]!) - 5} textAnchor="middle">
            {formatValue(totals[maxIdx]!)}
          </text>
        )}
      </svg>
      {hover !== null && (
        <Tooltip
          x={cx(hover)}
          y={top}
          containerWidth={width}
          content={{
            title: formatX(x[hover]!),
            rows: [
              ...series.map((s) => ({ label: s.label, value: formatValue(s.values[hover] ?? 0), color: slot(s.slot) })),
              ...(series.length > 1 && props.totalLabel ? [{ label: props.totalLabel, value: formatValue(totals[hover] ?? 0) }] : []),
            ],
          }}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------------ horizontal bar list (rankings, funnel)

export interface BarItem {
  label: string;
  value: number;
  /** CSS color; defaults to slot 1 (one series = one color). */
  color?: string;
  /** Secondary text after the value (e.g. share). */
  note?: string;
  tooltip?: TooltipContent;
}

export function BarList({ items, format, max, empty = 'Sin datos en este periodo', ariaLabel }: { items: BarItem[]; format: (v: number) => string; max?: number; empty?: string; ariaLabel: string }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<{ i: number; y: number } | null>(null);
  if (!items.length) return <div className="empty">{empty}</div>;
  const top = max ?? Math.max(...items.map((i) => i.value), 1);
  return (
    <div className="chart" ref={ref}>
      <div className="barlist" role="list" aria-label={ariaLabel}>
        {items.map((it, i) => (
          <div
            key={`${it.label}-${i}`}
            className="barlist-row"
            role="listitem"
            tabIndex={it.tooltip ? 0 : -1}
            onPointerEnter={(e) => it.tooltip && setHover({ i, y: (e.currentTarget as HTMLElement).offsetTop })}
            onPointerLeave={() => setHover(null)}
            onFocus={(e) => it.tooltip && setHover({ i, y: e.currentTarget.offsetTop })}
            onBlur={() => setHover(null)}
          >
            <span className="barlist-label" title={it.label}>
              {it.label}
            </span>
            <span className="barlist-track">
              <span className="barlist-bar" style={{ width: `calc((100% - 92px) * ${Math.max(0, it.value) / top})`, background: it.color ?? slot(1) }} />
              <span className="barlist-value">
                {format(it.value)}
                {it.note && <small>{it.note}</small>}
              </span>
            </span>
          </div>
        ))}
      </div>
      {hover && items[hover.i]?.tooltip && <Tooltip x={width * 0.45} y={hover.y + 20} containerWidth={width} content={items[hover.i]!.tooltip!} />}
    </div>
  );
}

// ------------------------------------------------------------------ sparkline

export function Sparkline({ values, height = 28 }: { values: number[]; height?: number }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  if (values.length < 2) return null;
  const max = Math.max(...values, 1);
  const w = Math.min(width, 96);
  const px = (i: number) => 2 + (i / (values.length - 1)) * (w - 6);
  const py = (v: number) => 3 + (height - 6) * (1 - v / max);
  const d = values.map((v, i) => `${i ? 'L' : 'M'}${px(i).toFixed(1)},${py(v).toFixed(1)}`).join('');
  const last = values.length - 1;
  return (
    <div ref={ref} style={{ width: '40%', maxWidth: 96, minWidth: 0 }} aria-hidden>
      <svg width={w} height={height}>
        <path d={d} fill="none" stroke="var(--deemph)" strokeWidth={1.5} strokeLinejoin="round" />
        <circle cx={px(last)} cy={py(values[last]!)} r={3} fill="var(--accent)" />
      </svg>
    </div>
  );
}

// ------------------------------------------------------------------ heatmap (sequential, binned)

export const HEAT_BINS = 7;

export function heatBin(value: number, max: number): number {
  if (!(max > 0)) return 1;
  return Math.min(HEAT_BINS, Math.max(1, Math.ceil((value / max) * HEAT_BINS)));
}

export interface HeatCell {
  value: number | null;
  text: string;
  /** Rate measured on part of the cohort only (maturity < 100%). */
  partial?: boolean;
  tooltip: TooltipContent;
}

export interface HeatRow {
  key: string;
  label: ReactNode;
  size: string;
  cells: HeatCell[];
}

export function Heatmap({ columns, rows, max, groupLabel, format, ariaLabel }: { columns: string[]; rows: HeatRow[]; max: number; groupLabel: string; format: (v: number) => string; ariaLabel: string }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<{ r: number; c: number; x: number; y: number } | null>(null);
  const show = (r: number, c: number, el: HTMLElement) => setHover({ r, c, x: el.offsetLeft + el.offsetWidth / 2, y: el.offsetTop + el.offsetHeight });
  const cell = hover ? rows[hover.r]?.cells[hover.c] : undefined;
  return (
    <div className="chart" ref={ref}>
      <div className="table-wrap">
        <table className="heatmap" aria-label={ariaLabel}>
          <thead>
            <tr>
              <th className="group" scope="col">
                {groupLabel}
              </th>
              <th scope="col">Cohorte</th>
              {columns.map((c) => (
                <th key={c} scope="col">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => (
              <tr key={row.key}>
                <td className="group">{row.label}</td>
                <td className="size">{row.size}</td>
                {row.cells.map((c, k) => {
                  const bin = c.value === null ? 0 : heatBin(c.value, max);
                  return (
                    <td
                      key={k}
                      className={`cell${c.value === null ? ' immature' : ''}${c.partial ? ' partial' : ''}`}
                      style={bin ? { background: `var(--seq-${bin})`, color: `var(--seq-ink-${bin})` } : undefined}
                      tabIndex={0}
                      onPointerEnter={(e) => show(r, k, e.currentTarget)}
                      onPointerLeave={() => setHover(null)}
                      onFocus={(e) => show(r, k, e.currentTarget)}
                      onBlur={() => setHover(null)}
                    >
                      {c.text}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="scale-legend" style={{ marginTop: 8 }}>
        <span>{format(0)}</span>
        <span className="swatches" aria-hidden>
          {Array.from({ length: HEAT_BINS }, (_, i) => (
            <i key={i} style={{ background: `var(--seq-${i + 1})` }} />
          ))}
        </span>
        <span>{format(max)}</span>
        <span>· “·” = cohorte aún no madura · * = menos de la mitad de la cohorte madura</span>
      </div>
      {hover && cell && <Tooltip x={hover.x} y={hover.y} containerWidth={width} content={cell.tooltip} />}
    </div>
  );
}

// ------------------------------------------------------------------ interval (forest) plot: differences against 0

export interface IntervalRow {
  label: string;
  value: number;
  low: number;
  high: number;
  /** CSS color of the mark (the variant's slot) */
  color?: string;
  /** "Mejor", "Peor"… shown after the value (text in ink, never the mark color) */
  note?: string;
}

/**
 * Each row is a difference with its interval, on one shared horizontal axis with the zero line marked: an interval
 * that does not cross the line is a clear difference. Values and labels are text; the mark is a 2px line with an
 * 8px dot.
 */
export function IntervalChart({ rows, format, ariaLabel }: { rows: IntervalRow[]; format: (v: number) => string; ariaLabel: string }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  if (!rows.length) return <div className="empty">Sin datos</div>;
  // a symmetric axis around 0 that ends on a round number
  const ticks = niceTicks(Math.max(...rows.flatMap((r) => [Math.abs(r.low), Math.abs(r.high), Math.abs(r.value)]), 1e-9), 2);
  const extent = ticks[ticks.length - 1]!;
  // phones: the label and the value go above each interval, which gets the whole width
  const narrow = width < 520;
  const labelW = narrow ? 8 : Math.min(150, Math.max(90, width * 0.22));
  const valueW = narrow ? 8 : Math.min(200, Math.max(120, width * 0.3));
  const plotW = Math.max(80, width - labelW - valueW - 16);
  const x = (v: number) => labelW + ((v + extent) / (2 * extent)) * plotW;
  const rowH = narrow ? 46 : 34;
  const height = rows.length * rowH + 22;
  return (
    <div className="chart" ref={ref}>
      <svg width={width} height={height} role="img" aria-label={ariaLabel}>
        <line className="grid-line" x1={x(-extent)} x2={x(extent)} y1={height - 18} y2={height - 18} />
        <line x1={x(0)} x2={x(0)} y1={4} y2={height - 18} stroke="var(--baseline)" strokeWidth={1} />
        <text x={x(0)} y={height - 4} textAnchor="middle" className="tick">
          0
        </text>
        <text x={x(-extent)} y={height - 4} textAnchor="start" className="tick">
          {format(-extent)}
        </text>
        <text x={x(extent)} y={height - 4} textAnchor="end" className="tick">
          {format(extent)}
        </text>
        {rows.map((r, i) => {
          const top = 6 + i * rowH;
          const cy = narrow ? top + rowH - 12 : top + rowH / 2;
          const color = r.color ?? slot(2);
          const value = `${format(r.value)}${r.note ? ` · ${r.note}` : ''}`;
          const label = r.label.length > 18 ? `${r.label.slice(0, 17)}…` : r.label;
          return (
            <g key={r.label}>
              <title>{`${r.label}: ${format(r.value)} (${format(r.low)} a ${format(r.high)})${r.note ? ` · ${r.note}` : ''}`}</title>
              {narrow ? (
                <text x={0} y={top + 12} className="end-label">
                  {label} · {value}
                </text>
              ) : (
                <>
                  <text x={0} y={cy + 4} className="end-label">
                    {label}
                  </text>
                  <text x={labelW + plotW + 12} y={cy + 4} className="end-label">
                    {value}
                  </text>
                </>
              )}
              <line x1={x(r.low)} x2={x(r.high)} y1={cy} y2={cy} stroke={color} strokeWidth={2} strokeLinecap="round" />
              <circle cx={x(r.value)} cy={cy} r={4} fill={color} stroke="var(--surface)" strokeWidth={2} />
            </g>
          );
        })}
      </svg>
    </div>
  );
}
